import React, { useState } from "react";

interface CardSearchProps {
  onCardSelect: (card: any) => void;
  buttonLabel?: string; // e.g. "Add to Inventory" or "Add to Wishlist"
}

const rarities = [
  "Amazing Rare",
  "Common",
  "LEGEND",
  "Promo",
  "Rare",
  "Rare ACE",
  "Rare BREAK",
  "Rare Holo",
  "Rare Holo EX",
  "Rare Holo GX",
  "Rare Holo LV.X",
  "Rare Holo Star",
  "Rare Holo V",
  "Rare Holo VMAX",
  "Rare Prime",
  "Rare Prism Star",
  "Rare Rainbow",
  "Rare Secret",
  "Rare Shining",
  "Rare Shiny GX",
  "Rare Ultra",
  "Rare Shiny",
  "Uncommon"
];

const supertypes = [
  "Pokémon",
  "Trainer",
  "Energy",
];

const subtypes = [
  "BREAK",
  "Baby",
  "Basic",
  "EX",
  "GX",
  "Goldenrod Game Corner",
  "Item",
  "LEGEND",
  "Level-Up",
  "MEGA",
  "Pokémon Tool",
  "Pokémon Tool F",
  "Rapid Strike",
  "Restored",
  "Rocket's Secret Machine",
  "Single Strike",
  "Special",
  "Stadium",
  "Stage 1",
  "Stage 2",
  "Supporter",
  "TAG TEAM",
  "Technical Machine",
  "V",
  "VMAX"
];

const CardSearch: React.FC<CardSearchProps> = ({
  onCardSelect,
  buttonLabel = "Add",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchSet, setSearchSet] = useState("");
  const [searchRarity, setSearchRarity] = useState("");
  const [searchSupertype, setSearchSupertype] = useState("");
  const [searchSubtype, setSearchSubtype] = useState(""); // <-- new state
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const searchCards = async () => {
    setLoading(true);
    setSearchError(null);
    try {
      let queryParts: string[] = [];

      // Only add name if provided
      let safeTerm = String(searchTerm || "").trim();
      if (safeTerm) {
        if (safeTerm.includes(" ")) safeTerm = `"${safeTerm}"`;
        queryParts.push(`name:${safeTerm}`);
      }
      if (searchSet) {
        const setValue = searchSet.includes(" ") ? `"${searchSet}"` : searchSet;
        queryParts.push(`set.name:${setValue}`);
      }
      if (searchRarity) {
        const rarityValue = searchRarity.includes(" ") ? `"${searchRarity}"` : searchRarity;
        queryParts.push(`rarity:${rarityValue}`);
      }
      if (searchSupertype) queryParts.push(`supertype:${searchSupertype}`);
      if (searchSubtype) queryParts.push(`subtypes:${searchSubtype}`);

      // If no filters, don't search
      if (queryParts.length === 0) {
        setSearchResults([]);
        setLoading(false);
        return;
      }

      const queryString = queryParts.join(" ");
      let queryUrl = `https://api.pokemontcg.io/v2/cards?q=${encodeURIComponent(queryString)}`;

      const res = await fetch(queryUrl, {
        headers: { "X-Api-Key": process.env.REACT_APP_POKEMON_API_KEY || "" },
      });
      const data = await res.json();
      setSearchResults(data.data || []);
    } catch (err) {
      setSearchError("Search failed. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div>
      <input
        type="text"
        className="border border-gray-300 rounded-lg w-full mb-2 px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none transition"
        placeholder="Search for a card name..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") searchCards(); }}
      />
      <div className="flex flex-col gap-2 mb-2">
        <input
          type="text"
          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none transition"
          placeholder="Set name"
          value={searchSet}
          onChange={e => setSearchSet(e.target.value)}
        />
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none transition"
          value={searchRarity}
          onChange={e => setSearchRarity(e.target.value)}
        >
          <option value="">All Rarities</option>
          {rarities.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none transition"
          value={searchSupertype}
          onChange={e => setSearchSupertype(e.target.value)}
        >
          <option value="">All Types</option>
          {supertypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none transition"
          value={searchSubtype}
          onChange={e => setSearchSubtype(e.target.value)}
        >
          <option value="">All Subtypes</option>
          {subtypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <button
        className="mb-4 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition"
        onClick={searchCards}
        disabled={loading || (!searchTerm && !searchSet && !searchRarity && !searchSupertype && !searchSubtype)}
      >
        Search
      </button>
      {loading && <div className="text-center text-gray-500">Loading...</div>}
      {searchError && <div className="text-center text-red-500">{searchError}</div>}
      <div className="max-h-64 overflow-y-auto">
        {searchResults.map(card => (
          <div
            key={card.id}
            className="flex items-center gap-2 border-b py-2 cursor-pointer hover:bg-indigo-50 transition"
            onClick={() => onCardSelect(card)}
          >
            <img src={card.images?.small} alt={card.name} className="w-12 h-16 object-contain rounded" />
            <div>
              <div className="font-bold text-indigo-700">{card.name}</div>
              <div className="text-xs text-gray-500">{card.set?.name} #{card.number}</div>
              <div className="text-xs text-gray-500">{card.rarity}</div>
            </div>
            <span className="ml-auto px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs">{buttonLabel}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CardSearch;